import React from 'react';
import moment from 'moment';
import { CmhcIcon, CmhcIconSize, CmhcFlexIcon } from '@cmhc-schl/hearth-ui';
import { Box, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import { MRT_ColumnDef } from 'material-react-table';
import { createColumn, createFirstColumn } from './TableCellRenderers';
import { CreateColumnsParams, RiskResult, ScenarioData, ScenarioType } from '../types';
import styles from './styles.module.scss';
import { getScenarioType } from '../utils/TransposedData';
import { LockAndUnlockFooter } from '../components/LockAndUnlockFooter';
import { IsLockedIndicator } from 'Feature/V2/HbtMasterPool/components/HbtMasterPoolDetails/utils/masterPoolConstants';
import { LegacyPoolScenarioColumns } from './LegacyPoolScenarioColumns';
import { MasterPoolStatusEnum } from 'Feature/V2/HbtMasterPool/components/HbtMasterPoolTable/types';
import { renderDataTestId } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementTable/vriTableColumns';

const sizeColumn = (scenarioNumber: number, language: string) => {
  if (scenarioNumber === 1) {
    return language === 'en' ? 360 : 360;
  }
  if (scenarioNumber === 2) {
    return language === 'en' ? 260 : 360;
  }
  if (scenarioNumber === 3) {
    return language === 'en' ? 270 : 360;
  }

  return language === 'en' ? 330 : 360;
};

const LEGACY_POOL_EXCLUDED_SCENARIOS = new Set([4, 5, 6]);
const legacyPoolIndicatorBefore2020TimeStamp = moment('Sept 26, 2020', 'MMM D, YYYY');

const shouldFilterLegacyPoolIndicatorZero2020To2026 = (indicator: number, timeStamp: string) => {
  const scenarioTimeStamp = moment(timeStamp, 'MMMM Do, YYYY');
  return indicator === 0 && scenarioTimeStamp.isAfter(legacyPoolIndicatorBefore2020TimeStamp);
};

export const getOrderedScenarioData = (
  data: RiskResult[],
  indicator: number,
  timeStamp: string,
  scenarioType: ScenarioType
): RiskResult[] => {
  if (!data || data.length === 0) {
    return [];
  }

  let orderedData = [...data];

  if (scenarioType === 'Default') {
    if (shouldFilterLegacyPoolIndicatorZero2020To2026(indicator, timeStamp)) {
      orderedData = orderedData.filter(
        (risk) => !LEGACY_POOL_EXCLUDED_SCENARIOS.has(risk.scenarioNumber)
      );
    }

    return customSort(orderedData);
  }

  return orderedData.sort((a, b) => a.scenarioNumber - b.scenarioNumber);
};

export const createColumns = ({
  t,
  language,
  indicator,
  timeStamp,
  data,
  handleFooterScenarioClick,
  showFooter,
  onModifyScenario,
  pricingScenarioLocales,
  isRisked,
  masterPoolStatusCode
}: CreateColumnsParams): MRT_ColumnDef<ScenarioData>[] => {
  const renderFirstColumn = createColumn(t);
  const rowHeader = createFirstColumn(t);
  const scenarioType = getScenarioType(data, indicator, timeStamp);
  const scenarioData = getOrderedScenarioData(data, indicator, timeStamp, scenarioType);
  const isMasterpoolInLockedStatus = masterPoolStatusCode === MasterPoolStatusEnum.Lock;

  const renderColumnActionsMenuItems = ({ column }: { column: any }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      setAnchorEl(e.currentTarget);
    };
    const handleCloseMenu = () => setAnchorEl(null);
    const scenarioNumber = column.columnDef.scenarioNumber as number;
    const handleModifyScenario = () => {
      onModifyScenario(scenarioNumber);
      handleCloseMenu();
    };
    const headerValue = column.columnDef.header;
    const headerText = typeof headerValue === 'string' ? headerValue : '';
    return (
      <Box>
        <span className={styles.scenarioHeaderText}>{headerText}</span>

        <Tooltip title={headerText}>
          <IconButton
            onClick={handleOpenMenu}
            className={styles.scenarioActionsButton}
            aria-label={`scenario-${scenarioNumber}-actions`}
          >
            <CmhcIcon size={CmhcIconSize.SMALL} type="icon_more_vertical" />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          onClick={(e) => e.stopPropagation()}
          className={styles.dataTableContainerPopupMenuContainer}
          sx={{
            minWidth: '250px',
            '.MuiMenuItem-root': {
              minHeight: '50px'
            },
            '.MuiPaper-root': {
              boxShadow: 'unset',
              border: '2px solid #697885',
              width: 'fit-content'
            }
          }}
        >
          <MenuItem
            className={styles.menuItem}
            key={`modify-${scenarioNumber}`}
            onClick={handleModifyScenario}
            data-testid={`modify-scenario-${scenarioNumber}-menu-item`}
          >
            <CmhcFlexIcon width={16} height={16} type="icon_edit" />
            <span>{pricingScenarioLocales('modify-scenario-header')}</span>
          </MenuItem>
          {/* <MenuItem key={`archive-${scenarioNumber}`} className={styles.menuItem}>
            <CmhcFlexIcon width={16} height={16} type="icon_download" />
            <span>{pricingScenarioLocales('archive-scenario-header')}</span>
          </MenuItem> */}
        </Menu>
      </Box>
    );
  };

  const headerColumn = {
    accessorKey: 'rowHeader' as const,
    header: '',
    Cell: rowHeader,
    enableColumnActions: false,
    size: language === 'en' ? 320 : 350,
    enableColumnOrdering: false,
    muiTableHeadCellProps: renderDataTestId('pricingScenarioRowHeader')
  };
  if (scenarioType === 'Default') {
    let scenario: Record<string, number> = {};
    const listOfScenario: MRT_ColumnDef<ScenarioData>[] = scenarioData.map((risk, index) => {
      let termInsuranceYearCount = risk.termInsuranceYearCount ?? 0;
      if (!(`termInsuranceYearCount${termInsuranceYearCount}` in scenario)) {
        scenario = {
          ...scenario,
          [`termInsuranceYearCount${termInsuranceYearCount}`]: 0
        };
      } else {
        scenario = {
          ...scenario,
          [`termInsuranceYearCount${termInsuranceYearCount}`]: ++scenario[
            `termInsuranceYearCount${termInsuranceYearCount}`
          ]
        };
      }
      let currentNum = scenario[`termInsuranceYearCount${termInsuranceYearCount}`];
      let header = `${t('Full-Amortization')} ${currentNum > 0 ? `(${currentNum})` : ''}`;
      if (risk.termInsuranceYearCount) {
        if (risk.termInsuranceYearCount === 1) {
          header = ` ${t('One-Year-Term-Insurance')} ${currentNum > 0 ? `(${currentNum})` : ''}`;
        } else {
          header = ` ${t('Year-Term-Insurance', { count: termInsuranceYearCount })} ${currentNum > 0 ? `(${currentNum})` : ''}`;
        }
      }
      let accessorKey = `yearTermInsurance${++index}`;

      if (risk.scenarioNumber <= 6) {
        header = t(`Scenario${risk.scenarioNumber}`);
      }

      const hasAnyLocked = scenarioData.some(
        (r) => r.lockIndicator === IsLockedIndicator.IsLockedIndicator
      );

      return {
        accessorKey: accessorKey,
        header,
        Cell: renderFirstColumn,
        size: language === 'en' ? 280 : 333,
        scenarioNumber: risk.scenarioNumber,
        lockIndicator: risk.lockIndicator,
        muiTableHeadCellProps:
          risk.lockIndicator === IsLockedIndicator.IsLockedIndicator && isMasterpoolInLockedStatus
            ? {
                sx: {
                  borderBottom: '4px solid #00093A',
                  fontSize: '16px'
                }
              }
            : undefined,
        Header: risk.scenarioNumber > 6 && isRisked ? renderColumnActionsMenuItems : undefined,
        Footer: () => {
          if (showFooter && risk.lockIndicator === IsLockedIndicator.IsLockedIndicator) {
            return (
              <LockAndUnlockFooter
                isUnlock={hasAnyLocked}
                risk={risk}
                onClick={handleFooterScenarioClick}
                t={t}
              />
            );
          } else if (showFooter && !hasAnyLocked) {
            return (
              <LockAndUnlockFooter
                isUnlock={hasAnyLocked}
                risk={risk}
                onClick={handleFooterScenarioClick}
                t={t}
              />
            );
          } else {
            return undefined;
          }
        },
        termInsuranceYearCount: risk.termInsuranceYearCount ?? 0
      };
    });

    return [headerColumn, ...listOfScenario];
  }

  if (scenarioType === 'LegacyPoolIndicatorZero2020To2026') {
    return LegacyPoolScenarioColumns({
      data: scenarioData,
      t,
      language,
      showFooter,
      renderFirstColumn,
      handleFooterScenarioClick,
      headerColumn,
      sizeColumn,
      isMasterpoolInLockedStatus
    });
  }

  const final = scenarioData.map((risk) => {
    const hasAnyLocked = scenarioData.some(
      (r) => r.lockIndicator === IsLockedIndicator.IsLockedIndicator
    );
    return {
      accessorKey: `scenario${risk.scenarioNumber}` as const,
      header: t(`Scenario${risk.scenarioNumber}`),
      Cell: renderFirstColumn,
      size: sizeColumn(risk.scenarioNumber, language),
      scenarioNumber: risk.scenarioNumber,
      lockIndicator: risk.lockIndicator,
      muiTableHeadCellProps:
        risk.lockIndicator === IsLockedIndicator.IsLockedIndicator && isMasterpoolInLockedStatus
          ? {
              sx: {
                borderBottom: '4px solid #00093A',
                fontSize: '16px'
              }
            }
          : undefined,
      Footer: () => {
        if (showFooter && risk.lockIndicator === IsLockedIndicator.IsLockedIndicator) {
          return (
            <LockAndUnlockFooter
              isUnlock={hasAnyLocked}
              risk={risk}
              onClick={handleFooterScenarioClick}
              t={t}
            />
          );
        } else if (showFooter && !hasAnyLocked) {
          return (
            <LockAndUnlockFooter
              isUnlock={hasAnyLocked}
              risk={risk}
              onClick={handleFooterScenarioClick}
              t={t}
            />
          );
        } else {
          return undefined;
        }
      }
    };
  });

  return [headerColumn, ...final.sort((a, b) => a.scenarioNumber - b.scenarioNumber)];
};

export const customSort = (data: RiskResult[]): RiskResult[] => {
  const baseScenarios = data.filter((item) => (item.scenarioNumber ?? 0) <= 6);
  const termInsuranceScenarios = data.filter((item) => (item.scenarioNumber ?? 0) > 6);

  const sortedTermInsurance = sortTermInsuranceScenarios(termInsuranceScenarios);

  return [...baseScenarios, ...sortedTermInsurance];
};

const sortTermInsuranceScenarios = (data: RiskResult[]): RiskResult[] => {
  const seenTermInsuranceYearCount = new Set<number>();
  const duplicateTermInsuranceYearCount: number[] = [];
  const uniqueItems: RiskResult[] = [];
  const duplicateItems: RiskResult[] = [];
  const itemsShowUpMoreThanTwoTimes: RiskResult[] = [];

  for (const item of data) {
    if (!seenTermInsuranceYearCount.has(item.termInsuranceYearCount ?? 0)) {
      uniqueItems.push(item);
      seenTermInsuranceYearCount.add(item.termInsuranceYearCount ?? 0);
    } else if (duplicateTermInsuranceYearCount.includes(item.termInsuranceYearCount ?? 0)) {
      itemsShowUpMoreThanTwoTimes.push(item);
    } else {
      duplicateItems.push(item);
      duplicateTermInsuranceYearCount.push(item.termInsuranceYearCount ?? 0);
    }
  }

  let sortedRisResult: RiskResult[] = [];
  if (itemsShowUpMoreThanTwoTimes.length > 0) {
    sortedRisResult = sortTermInsuranceScenarios(itemsShowUpMoreThanTwoTimes);
  }

  uniqueItems.sort((a, b) => sort(a.termInsuranceYearCount ?? 0, b.termInsuranceYearCount ?? 0));
  duplicateItems.sort((a, b) => sort(a.termInsuranceYearCount ?? 0, b.termInsuranceYearCount ?? 0));

  return [...uniqueItems, ...duplicateItems, ...sortedRisResult];
};

const sort = (a: number, b: number) => {
  return a - b;
};
